import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import ReviewDialog from '../components/ReviewDialog';
import {
  Package, CheckCircle2, Truck, XCircle, Clock, ExternalLink, ChevronRight, ReceiptText, Star, RotateCcw, AlertTriangle, MapPin, Copy, Download,
} from 'lucide-react';
import { resolveAssetUrl } from '../lib/assets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:   { lv: 'Gaida apstiprinājumu', en: 'Awaiting confirmation', ru: 'Ожидает подтверждения', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  confirmed: { lv: 'Apstiprināts',         en: 'Confirmed',             ru: 'Подтверждён',           color: 'bg-blue-100 text-blue-800 border-blue-200',    icon: CheckCircle2 },
  shipped:   { lv: 'Nosūtīts',             en: 'Shipped',               ru: 'Отправлен',             color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Truck },
  delivered: { lv: 'Piegādāts',            en: 'Delivered',             ru: 'Доставлен',             color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 },
  cancelled: { lv: 'Atcelts',              en: 'Cancelled',             ru: 'Отменён',               color: 'bg-red-100 text-red-800 border-red-200',       icon: XCircle },
  paid:      { lv: 'Apmaksāts',            en: 'Paid',                  ru: 'Оплачен',               color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
};

// Allowed state transitions per role (mirrors backend)
const TRANSITIONS = {
  seller: {
    pending:   [{ to: 'confirmed', lv: 'Apstiprināt',    en: 'Confirm', ru: 'Подтвердить' },
                { to: 'cancelled', lv: 'Atcelt',         en: 'Cancel', ru: 'Отменить' }],
    confirmed: [{ to: 'shipped',   lv: 'Atzīmēt kā nosūtītu', en: 'Mark as shipped', ru: 'Отметить как отправленный' },
                { to: 'cancelled', lv: 'Atcelt',         en: 'Cancel', ru: 'Отменить' }],
    shipped:   [{ to: 'delivered', lv: 'Atzīmēt kā piegādātu', en: 'Mark as delivered', ru: 'Отметить как доставленный' }],
  },
  buyer: {
    shipped:   [{ to: 'delivered', lv: 'Apstiprināt saņemšanu', en: 'Confirm receipt', ru: 'Подтвердить получение' }],
    pending:   [{ to: 'cancelled', lv: 'Atcelt',         en: 'Cancel', ru: 'Отменить' }],
    confirmed: [{ to: 'cancelled', lv: 'Atcelt',         en: 'Cancel', ru: 'Отменить' }],
  },
};

function StatusBadge({ status }) {
  const { language } = useLanguage();
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`text-xs font-medium ${meta.color} gap-1.5`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />
      {meta[language] || meta.en}
    </Badge>
  );
}

function OrderCard({ order, mode, onAction, onRefresh, disputes = [] }) {
  const { language } = useLanguage();
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [activeReviewItem, setActiveReviewItem] = useState(null);
  const [buyerReviewOpen, setBuyerReviewOpen] = useState(false);
  const [buyerReviewed, setBuyerReviewed] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  // Manual shipment tracking (seller enters at ship time)
  const [shipOpen, setShipOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');
  const [trackingUrl, setTrackingUrl] = useState('');
  // Partial refund (per-item) state
  const [refundItem, setRefundItem] = useState(null); // {listing_id, title, quantity, price, max_refundable}
  const [refundReason, setRefundReason] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);
  // Dispute (refund request) state
  const [disputeItem, setDisputeItem] = useState(null); // {listing_id, title}
  const [disputeCategory, setDisputeCategory] = useState('damaged');
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeBusy, setDisputeBusy] = useState(false);
  // Seller response to dispute
  const [respondingDispute, setRespondingDispute] = useState(null);
  const [disputeResponse, setDisputeResponse] = useState('');
  const [disputeAction, setDisputeAction] = useState(null); // 'approve' | 'reject'
  // Map listing_id -> true if the current user already reviewed that listing.
  const [reviewed, setReviewed] = useState({});

  const status = order.status || 'pending';
  const workflowStatus = status === 'paid' ? 'pending' : status;
  const isDelivered = workflowStatus === 'delivered';
  const canReview = mode === 'buyer' && isDelivered;
  const canReviewBuyer = mode === 'seller' && isDelivered;
  const actions = (TRANSITIONS[mode] || {})[workflowStatus] || [];

  // Check review status for delivered orders (buyer: per-listing, seller: per-order buyer review)
  useEffect(() => {
    let cancelled = false;
    if (canReview) {
      (async () => {
        const result = {};
        for (const it of order.items || []) {
          if (!it.listing_id) continue;
          try {
            const { data } = await axios.get(`${API}/reviews/mine/${it.listing_id}`);
            result[it.listing_id] = !!(data && data.exists);
          } catch { /* ignore */ }
        }
        if (!cancelled) setReviewed(result);
      })();
    } else if (canReviewBuyer) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/buyer-reviews/mine/${order.id}`);
          if (!cancelled) setBuyerReviewed(!!(data && data.exists));
        } catch { /* ignore */ }
      })();
    }
    return () => { cancelled = true; };
  }, [canReview, canReviewBuyer, order.items, order.id]);

  const openReview = (item) => {
    setActiveReviewItem(item);
    setReviewOpen(true);
  };

  // Compute how many units of a given line item have already been refunded.
  const refundedQtyFor = (listingId) => {
    const refunds = order.item_refunds || [];
    return refunds
      .filter((r) => r.listing_id === listingId && (r.status === 'ok' || r.status === 'no_payment_intent' || r.status === 'not_paid'))
      .reduce((sum, r) => sum + (r.quantity || 0), 0);
  };

  // Seller (or admin) can partial-refund per-item only while the order is paid
  // and not terminal (cancelled). Delivered orders may still need refunds for
  // damaged items.
  const canPartialRefund = (mode === 'seller') &&
    order.payment_status === 'paid' &&
    workflowStatus !== 'cancelled';

  // Buyer can open a dispute (refund request) on paid, not-cancelled orders.
  const canOpenDispute = (mode === 'buyer') &&
    order.payment_status === 'paid' &&
    workflowStatus !== 'cancelled';

  // Buyer can download a VAT invoice PDF for any paid order.
  const canDownloadInvoice = (mode === 'buyer') && order.payment_status === 'paid';
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const downloadInvoice = async () => {
    setInvoiceBusy(true);
    try {
      const res = await axios.get(`${API}/orders/${order.id}/invoice`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `kolv-rekins-${order.id.slice(-6).toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(language === 'lv' ? 'Neizdevās lejupielādēt rēķinu' : language === 'ru' ? 'Не удалось скачать счёт' : 'Failed to download invoice');
    } finally {
      setInvoiceBusy(false);
    }
  };

  // Find open or resolved dispute for a given listing in this order
  const disputeForItem = (listingId) =>
    disputes.find((d) => d.listing_id === listingId);

  const submitDispute = async () => {
    if (!disputeItem) return;
    if ((disputeReason || '').trim().length < 10) {
      toast.error(language === 'lv'
        ? 'Pamatojumam jābūt vismaz 10 rakstzīmēm'
        : language === 'ru'
        ? 'Обоснование должно содержать не менее 10 символов'
        : 'Reason must be at least 10 characters');
      return;
    }
    setDisputeBusy(true);
    try {
      await axios.post(`${API}/orders/${order.id}/disputes`, {
        listing_id: disputeItem.listing_id,
        category: disputeCategory,
        reason: disputeReason.trim(),
      });
      toast.success(language === 'lv'
        ? 'Atmaksas pieprasījums nosūtīts pārdevējam'
        : language === 'ru'
        ? 'Запрос на возврат отправлен продавцу'
        : 'Refund request sent to seller');
      setDisputeItem(null);
      setDisputeReason('');
      setDisputeCategory('damaged');
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || (language === 'lv' ? 'Kļūda' : language === 'ru' ? 'Ошибка' : 'Error'));
    } finally {
      setDisputeBusy(false);
    }
  };

  const submitDisputeResponse = async () => {
    if (!respondingDispute || !disputeAction) return;
    if (disputeAction === 'reject' && (disputeResponse || '').trim().length < 10) {
      toast.error(language === 'lv'
        ? 'Noraidījuma pamatojumam jābūt vismaz 10 rakstzīmēm'
        : language === 'ru'
        ? 'Обоснование отклонения должно содержать не менее 10 символов'
        : 'Rejection reason must be at least 10 characters');
      return;
    }
    setDisputeBusy(true);
    try {
      const url = `${API}/disputes/${respondingDispute.id}/${disputeAction}`;
      const { data } = await axios.post(url, { response: disputeResponse.trim() });
      if (disputeAction === 'approve') {
        const amount = (data?.refund?.amount || 0).toFixed(2);
        toast.success(language === 'lv'
          ? `Atmaksa apstiprināta. Pircējam atmaksāti €${amount}`
          : language === 'ru'
          ? `Возврат подтверждён. Покупателю возвращено €${amount}`
          : `Refund approved. €${amount} sent to buyer`);
      } else {
        toast.success(language === 'lv'
          ? 'Atmaksas pieprasījums noraidīts'
          : language === 'ru'
          ? 'Запрос на возврат отклонён'
          : 'Refund request rejected');
      }
      setRespondingDispute(null);
      setDisputeResponse('');
      setDisputeAction(null);
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || (language === 'lv' ? 'Kļūda' : language === 'ru' ? 'Ошибка' : 'Error'));
    } finally {
      setDisputeBusy(false);
    }
  };

  const submitPartialRefund = async () => {
    if (!refundItem) return;
    setRefundBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/orders/${order.id}/refund-item`,
        { listing_id: refundItem.listing_id, reason: refundReason || undefined }
      );
      const status = data?.refund?.status;
      const method = data?.refund?.method;
      const amount = (data?.refund?.amount || 0).toFixed(2);
      if (status === 'ok' && method === 'wallet') {
        toast.success(language === 'lv'
          ? `Atmaksāts €${amount} pircēja KoLV maciņā`
          : language === 'ru'
          ? `Возвращено €${amount} в кошелёк KoLV покупателя`
          : `Refunded €${amount} to buyer's KoLV wallet`);
      } else if (status === 'ok' && method === 'stripe') {
        toast.success(language === 'lv'
          ? `Atmaksa €${amount} nosūtīta uz pircēja karti (5–10 d.d.)`
          : language === 'ru'
          ? `Возврат €${amount} отправлен на карту покупателя (5–10 дн.)`
          : `Refund €${amount} issued to buyer's card (5–10 days)`);
      } else if (status === 'failed') {
        toast.warning(language === 'lv'
          ? 'Automātiskā atmaksa neizdevās. Apstrādās manuāli.'
          : language === 'ru'
          ? 'Автоматический возврат не удался. Будет обработан вручную.'
          : 'Automatic refund failed. Will be processed manually.');
      } else {
        toast.success(language === 'lv' ? 'Atmaksa reģistrēta' : language === 'ru' ? 'Возврат зарегистрирован' : 'Refund recorded');
      }
      setRefundItem(null);
      setRefundReason('');
      // Trigger parent reload via onAction with a synthetic no-op? Simpler:
      // call onAction with current status -- but that would push history. Skip.
      // Just mutate the local order via a window event would be hacky. Easiest:
      // page reload via parent's load() by passing onAction(null, null, null)? No.
      // Best: expose a `onRefreshed` callback. For now, reload the page state via
      // window.location.reload — too aggressive. Use a small trick: pass `noop`.
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || (language === 'lv' ? 'Kļūda' : language === 'ru' ? 'Ошибка' : 'Error'));
    } finally {
      setRefundBusy(false);
    }
  };

  const handleClick = async (toStatus) => {
    // Seller shipping → collect optional tracking info first.
    if (toStatus === 'shipped' && mode === 'seller') {
      setShipOpen(true);
      return;
    }
    setBusy(true);
    try {
      await onAction(order.id, toStatus, note);
      setNote('');
      setShowNote(false);
    } finally {
      setBusy(false);
    }
  };

  const confirmShip = async () => {
    setBusy(true);
    try {
      await onAction(order.id, 'shipped', note, {
        tracking_number: trackingNumber.trim(),
        tracking_carrier: trackingCarrier,
        tracking_url: trackingUrl.trim(),
      });
      setNote('');
      setShowNote(false);
      setShipOpen(false);
      setTrackingNumber(''); setTrackingCarrier(''); setTrackingUrl('');
    } finally {
      setBusy(false);
    }
  };

  const created = order.created_at ? new Date(order.created_at).toLocaleString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '';
  const items = order.items || [];

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5 sm:p-6" data-testid={`order-card-${order.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-outfit text-lg font-semibold text-[#121212]">
              {language === 'lv' ? 'Pasūtījums' : language === 'ru' ? 'Заказ' : 'Order'} #{order.id.slice(-6).toUpperCase()}
            </h3>
            <StatusBadge status={workflowStatus} />
          </div>
          <p className="text-xs text-[#5C5C5C] mt-1">{created}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-[#5C5C5C] uppercase tracking-wide">
            {language === 'lv' ? 'Kopā' : language === 'ru' ? 'Итого' : 'Total'}
          </p>
          <p className="font-outfit text-xl font-bold text-[#D97736]">
            €{(order.total || 0).toFixed(2)}
          </p>
          {canDownloadInvoice && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadInvoice}
              disabled={invoiceBusy}
              className="mt-2 border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white"
              data-testid={`download-invoice-${order.id}`}
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {language === 'lv' ? 'Rēķins' : language === 'ru' ? 'Счёт' : 'Invoice'}
            </Button>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="space-y-2 mb-4">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b border-[#F3F2EE] last:border-0">
            {it.image ? (
              <img src={resolveAssetUrl(it.image)} alt={it.title} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-[#F3F2EE] flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-[#5C5C5C]" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <Link
                to={it.listing_id ? `/listing/${it.listing_id}` : '#'}
                className="text-sm font-medium text-[#121212] hover:text-[#D97736] line-clamp-1"
              >
                {it.title || it.listing_title || '—'}
              </Link>
              <p className="text-xs text-[#5C5C5C]">
                {mode === 'seller' ? (
                  <>{language === 'lv' ? 'Pircējs' : language === 'ru' ? 'Покупатель' : 'Buyer'}: {order.user_name}</>
                ) : (
                  <>{language === 'lv' ? 'Pārdevējs' : language === 'ru' ? 'Продавец' : 'Seller'}: {it.seller_name || '—'}</>
                )}
                {' · '}{language === 'lv' ? 'Daudz.' : language === 'ru' ? 'Кол-во' : 'Qty'}: {it.quantity || 1}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-semibold text-[#121212]">
                €{((it.price || 0) * (it.quantity || 1)).toFixed(2)}
              </p>
              {/* Partial-refund status / button (seller view only) */}
              {(() => {
                const refundedQty = refundedQtyFor(it.listing_id);
                const orderedQty = it.quantity || 1;
                const fullyRefunded = refundedQty >= orderedQty;
                const dispute = disputeForItem(it.listing_id);

                // Refunded badge (replaces everything else for this item)
                if (refundedQty > 0 && fullyRefunded) {
                  return (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-red-700 mt-1 font-medium"
                      data-testid={`refunded-${it.listing_id}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                      {language === 'lv' ? 'Atmaksāts' : language === 'ru' ? 'Возвращено' : 'Refunded'}
                    </span>
                  );
                }
                if (refundedQty > 0 && !fullyRefunded) {
                  return (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-amber-700 mt-1"
                      data-testid={`partial-refunded-${it.listing_id}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                      {language === 'lv' ? `Atmaksāts ${refundedQty}/${orderedQty}` : language === 'ru' ? `Возвращено ${refundedQty}/${orderedQty}` : `Refunded ${refundedQty}/${orderedQty}`}
                    </span>
                  );
                }

                // Dispute badges
                if (dispute && dispute.status === 'open') {
                  // Seller: show "Atbildēt" button
                  if (mode === 'seller') {
                    return (
                      <button
                        type="button"
                        onClick={() => { setRespondingDispute(dispute); setDisputeAction('approve'); }}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-800 mt-1 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200"
                        data-testid={`respond-dispute-${dispute.id}`}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        {language === 'lv' ? 'Atbildēt uz strīdu' : language === 'ru' ? 'Ответить на спор' : 'Respond to dispute'}
                      </button>
                    );
                  }
                  // Buyer: show "Pending" badge
                  return (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-amber-700 mt-1 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200"
                      data-testid={`dispute-open-${dispute.id}`}
                    >
                      <Clock className="w-3 h-3" />
                      {language === 'lv' ? 'Strīds nosūtīts' : language === 'ru' ? 'Спор на рассмотрении' : 'Dispute pending'}
                    </span>
                  );
                }
                if (dispute && dispute.status === 'rejected') {
                  return (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] text-red-700 mt-1"
                      data-testid={`dispute-rejected-${dispute.id}`}
                      title={dispute.response || ''}
                    >
                      <XCircle className="w-3 h-3" />
                      {language === 'lv' ? 'Strīds noraidīts' : language === 'ru' ? 'Спор отклонён' : 'Dispute rejected'}
                    </span>
                  );
                }

                // Seller: partial-refund button
                if (canPartialRefund && it.listing_id) {
                  return (
                    <button
                      type="button"
                      onClick={() => setRefundItem({
                        listing_id: it.listing_id,
                        title: it.title || it.listing_title || '—',
                        quantity: orderedQty - refundedQty,
                        price: it.price || 0,
                      })}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-red-600 hover:text-red-700 mt-1"
                      data-testid={`refund-item-${it.listing_id}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                      {language === 'lv' ? 'Atmaksāt' : language === 'ru' ? 'Вернуть' : 'Refund'}
                    </button>
                  );
                }

                // Buyer: open dispute button
                if (canOpenDispute && it.listing_id) {
                  return (
                    <button
                      type="button"
                      onClick={() => setDisputeItem({
                        listing_id: it.listing_id,
                        title: it.title || it.listing_title || '—',
                      })}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:text-amber-800 mt-1"
                      data-testid={`open-dispute-${it.listing_id}`}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      {language === 'lv' ? 'Pieprasīt atmaksu' : language === 'ru' ? 'Запросить возврат' : 'Request refund'}
                    </button>
                  );
                }
                return null;
              })()}
              {canReview && it.listing_id && (
                reviewed[it.listing_id] ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-green-700 mt-1" data-testid={`reviewed-${it.listing_id}`}>
                    <Star className="w-3 h-3 fill-green-700" />
                    {language === 'lv' ? 'Novērtēts' : language === 'ru' ? 'Оценено' : 'Reviewed'}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => openReview(it)}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-[#D97736] hover:text-[#C06528] mt-1"
                    data-testid={`leave-review-${it.listing_id}`}
                  >
                    <Star className="w-3 h-3" />
                    {language === 'lv' ? 'Atstāt atsauksmi' : language === 'ru' ? 'Оставить отзыв' : 'Leave review'}
                  </button>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Shipping address — visible to buyer (their address) and seller (where to ship) */}
      {order.shipping_address && (order.shipping_address.address || order.shipping_address.name) && (
        <div className="mb-4 p-3 rounded-xl bg-[#F3F2EE] border border-[#E5E4E0]" data-testid={`shipping-address-${order.id}`}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-1.5 text-xs font-medium text-[#4A6B53]">
              <MapPin className="w-3.5 h-3.5" />
              {mode === 'seller'
                ? (language === 'lv' ? 'Sūtīt uz' : language === 'ru' ? 'Отправить на' : 'Ship to')
                : (language === 'lv' ? 'Piegādes adrese' : language === 'ru' ? 'Адрес доставки' : 'Delivery address')}
            </div>
            {mode === 'seller' && (
              <button
                type="button"
                onClick={() => {
                  const sa = order.shipping_address;
                  const text = [sa.name, sa.address, sa.postal_code, sa.phone].filter(Boolean).join(', ');
                  navigator.clipboard?.writeText(text)
                    .then(() => toast.success(language === 'lv' ? 'Adrese nokopēta' : language === 'ru' ? 'Адрес скопирован' : 'Address copied'))
                    .catch(() => toast.error(language === 'lv' ? 'Neizdevās nokopēt' : language === 'ru' ? 'Не удалось скопировать' : 'Copy failed'));
                }}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-[#D97736] hover:text-[#C06528]"
                data-testid={`copy-address-${order.id}`}
              >
                <Copy className="w-3 h-3" />
                {language === 'lv' ? 'Kopēt' : language === 'ru' ? 'Копировать' : 'Copy'}
              </button>
            )}
          </div>
          <div className="text-sm text-[#121212] leading-snug">
            {order.shipping_address.name && <span className="font-medium">{order.shipping_address.name}<br /></span>}
            {order.shipping_address.address}
            {order.shipping_address.postal_code ? `, ${order.shipping_address.postal_code}` : ''}
            {order.shipping_address.phone && (
              <span className="block text-[#5C5C5C]">
                {language === 'lv' ? 'Tālr.' : language === 'ru' ? 'Тел.' : 'Tel.'}: {order.shipping_address.phone}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Shipment tracking — shown once the seller attaches a tracking number */}
      {order.tracking && (order.tracking.number || order.tracking.url) && (
        <div className="mb-4 p-3 rounded-xl bg-[#EEF4EF] border border-[#D5E3D8]" data-testid={`tracking-${order.id}`}>
          <div className="flex items-center gap-1.5 text-xs font-medium text-[#4A6B53] mb-1">
            <Truck className="w-3.5 h-3.5" />
            {language === 'lv' ? 'Sūtījuma izsekošana' : language === 'ru' ? 'Отслеживание посылки' : 'Shipment tracking'}
          </div>
          <div className="text-sm text-[#121212]">
            {order.tracking.carrier && <span className="text-[#5C5C5C]">{order.tracking.carrier} · </span>}
            {order.tracking.number && <span className="font-mono">{order.tracking.number}</span>}
            {order.tracking.url && (
              <a href={order.tracking.url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 ml-2 text-[#D97736] hover:underline font-medium"
                 data-testid={`track-link-${order.id}`}>
                {language === 'lv' ? 'Izsekot' : language === 'ru' ? 'Отследить' : 'Track'} <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}

      {canReviewBuyer && (
        <div className="mb-4 pb-3 border-b border-[#F3F2EE]">
          {buyerReviewed ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-green-700" data-testid={`buyer-reviewed-${order.id}`}>
              <Star className="w-3.5 h-3.5 fill-green-700" />
              {language === 'lv' ? `Pircējs ${order.user_name || ''} novērtēts` : language === 'ru' ? `Покупатель ${order.user_name || ''} оценён` : `Buyer ${order.user_name || ''} rated`}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setBuyerReviewOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#D97736] hover:text-[#C06528]"
              data-testid={`rate-buyer-${order.id}`}
            >
              <Star className="w-3.5 h-3.5" />
              {language === 'lv' ? `Novērtēt pircēju ${order.user_name || ''}` : language === 'ru' ? `Оценить покупателя ${order.user_name || ''}` : `Rate buyer ${order.user_name || ''}`}
            </button>
          )}
        </div>
      )}

      {/* Status history timeline */}
      {Array.isArray(order.status_history) && order.status_history.length > 0 && (
        <details className="mb-4">
          <summary className="text-xs text-[#5C5C5C] cursor-pointer hover:text-[#121212] flex items-center gap-1" data-testid={`toggle-history-${order.id}`}>
            <ReceiptText className="w-3.5 h-3.5" />
            {language === 'lv' ? 'Statusu vēsture' : language === 'ru' ? 'История статусов' : 'Status history'} ({order.status_history.length})
          </summary>
          <ol className="mt-2 space-y-1 border-l-2 border-[#E5E4E0] pl-3 text-xs">
            {order.status_history.map((h, i) => (
              <li key={i} className="text-[#5C5C5C]">
                <span className="font-medium text-[#121212]">
                  {STATUS_META[h.status]?.[language] || STATUS_META[h.status]?.en || h.status}
                </span>
                {' — '}
                <span className="capitalize">{h.role}</span>
                {' · '}{new Date(h.at).toLocaleString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                {h.note && <p className="italic mt-0.5 ml-1">"{h.note}"</p>}
              </li>
            ))}
          </ol>
        </details>
      )}

      {/* Action buttons */}
      {actions.length > 0 && (
        <div className="flex flex-col gap-2 pt-3 border-t border-[#F3F2EE]">
          {showNote && (
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={language === 'lv' ? 'Piezīme (neobligāti)' : language === 'ru' ? 'Заметка (необязательно)' : 'Note (optional)'}
              rows={2}
              className="text-sm border-[#E5E4E0] focus:border-[#D97736]"
              data-testid={`order-note-${order.id}`}
            />
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {!showNote && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setShowNote(true)}
                className="text-xs text-[#5C5C5C] hover:text-[#121212]"
              >
                + {language === 'lv' ? 'Pievienot piezīmi' : language === 'ru' ? 'Добавить заметку' : 'Add note'}
              </Button>
            )}
            {actions.map((a) => {
              if (a.to === 'cancelled') {
                return (
                  <Button
                    key={a.to}
                    type="button"
                    size="sm"
                    disabled={busy}
                    onClick={() => setCancelOpen(true)}
                    className="bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
                    data-testid={`action-${a.to}-${order.id}`}
                  >
                    {a[language] || a.en}
                    <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                );
              }
              return (
                <Button
                  key={a.to}
                  type="button"
                  size="sm"
                  disabled={busy}
                  onClick={() => handleClick(a.to)}
                  className="bg-[#D97736] hover:bg-[#C06528] text-white"
                  data-testid={`action-${a.to}-${order.id}`}
                >
                  {a[language] || a.en}
                  <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Review dialog rendered per-card so it can access activeReviewItem */}
      <ReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        listingId={activeReviewItem?.listing_id}
        listingTitle={activeReviewItem?.title || activeReviewItem?.listing_title}
        onSubmitted={() => {
          if (activeReviewItem?.listing_id) {
            setReviewed((prev) => ({ ...prev, [activeReviewItem.listing_id]: true }));
          }
        }}
      />

      {/* Seller → buyer review dialog */}
      {canReviewBuyer && (
        <ReviewDialog
          mode="buyer"
          open={buyerReviewOpen}
          onOpenChange={setBuyerReviewOpen}
          orderId={order.id}
          buyerId={order.user_id}
          buyerName={order.user_name}
          onSubmitted={() => setBuyerReviewed(true)}
        />
      )}

      {/* Buyer: open refund dispute dialog */}
      <AlertDialog open={!!disputeItem} onOpenChange={(open) => !open && setDisputeItem(null)}>
        <AlertDialogContent data-testid={`open-dispute-dialog-${order.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'lv' ? 'Pieprasīt atmaksu' : language === 'ru' ? 'Запросить возврат' : 'Request refund'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-[#5C5C5C]">
                <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-lg p-3 text-[#121212]">
                  <p className="font-medium">{disputeItem?.title}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#121212] mb-1 block">
                    {language === 'lv' ? 'Iemesls' : language === 'ru' ? 'Причина' : 'Category'}
                  </label>
                  <select
                    value={disputeCategory}
                    onChange={(e) => setDisputeCategory(e.target.value)}
                    className="w-full text-sm border border-[#E5E4E0] rounded-md px-3 py-2 bg-white focus:border-[#D97736] focus:outline-none"
                    data-testid={`dispute-category-${order.id}`}
                  >
                    <option value="damaged">{language === 'lv' ? 'Prece bojāta' : language === 'ru' ? 'Товар повреждён' : 'Damaged'}</option>
                    <option value="not_received">{language === 'lv' ? 'Prece nav saņemta' : language === 'ru' ? 'Товар не получен' : 'Not received'}</option>
                    <option value="wrong_item">{language === 'lv' ? 'Saņemta nepareiza prece' : language === 'ru' ? 'Получен не тот товар' : 'Wrong item'}</option>
                    <option value="not_as_described">{language === 'lv' ? 'Neatbilst aprakstam' : language === 'ru' ? 'Не соответствует описанию' : 'Not as described'}</option>
                    <option value="other">{language === 'lv' ? 'Cits iemesls' : language === 'ru' ? 'Другая причина' : 'Other'}</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#121212] mb-1 block">
                    {language === 'lv' ? 'Pamatojums (vismaz 10 rakstzīmes)' : language === 'ru' ? 'Обоснование (минимум 10 символов)' : 'Reason (min 10 chars)'}
                  </label>
                  <Textarea
                    value={disputeReason}
                    onChange={(e) => setDisputeReason(e.target.value)}
                    placeholder={language === 'lv'
                      ? 'Aprakstī situāciju pārdevējam...'
                      : language === 'ru'
                      ? 'Опишите ситуацию продавцу...'
                      : 'Describe the issue to the seller...'}
                    rows={3}
                    className="text-sm"
                    data-testid={`dispute-reason-${order.id}`}
                  />
                </div>
                <p className="text-xs text-[#5C5C5C] italic">
                  {language === 'lv'
                    ? 'Pārdevējs saņems paziņojumu un varēs apstiprināt vai noraidīt atmaksu.'
                    : language === 'ru'
                    ? 'Продавец получит уведомление и сможет подтвердить или отклонить возврат.'
                    : 'The seller will be notified and can approve or reject the refund.'}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disputeBusy} data-testid={`dispute-dismiss-${order.id}`}>
              {language === 'lv' ? 'Atpakaļ' : language === 'ru' ? 'Назад' : 'Back'}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={disputeBusy}
              onClick={submitDispute}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid={`dispute-submit-${order.id}`}
            >
              {language === 'lv' ? 'Nosūtīt pārdevējam' : language === 'ru' ? 'Отправить продавцу' : 'Send to seller'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Seller: respond to refund dispute dialog */}
      <AlertDialog
        open={!!respondingDispute}
        onOpenChange={(open) => { if (!open) { setRespondingDispute(null); setDisputeAction(null); setDisputeResponse(''); } }}
      >
        <AlertDialogContent data-testid={`respond-dispute-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'lv' ? 'Atmaksas pieprasījums' : language === 'ru' ? 'Запрос на возврат' : 'Refund request'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-[#5C5C5C]">
                <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-lg p-3 text-[#121212]">
                  <p className="font-medium">{respondingDispute?.item_title}</p>
                  <p className="text-xs text-[#5C5C5C] mt-1">
                    {language === 'lv' ? 'Pircējs' : language === 'ru' ? 'Покупатель' : 'Buyer'}: {respondingDispute?.buyer_name}
                    {' · '}
                    {language === 'lv' ? 'Summa' : language === 'ru' ? 'Сумма' : 'Amount'}:{' '}
                    <span className="font-bold text-[#D97736]">
                      €{((respondingDispute?.item_price || 0) * (respondingDispute?.item_quantity || 0)).toFixed(2)}
                    </span>
                  </p>
                </div>
                <div className="border-l-4 border-amber-400 bg-amber-50 p-3 rounded-r-md">
                  <p className="text-xs font-semibold text-amber-900 mb-1">
                    {language === 'lv' ? 'Pircēja pamatojums:' : language === 'ru' ? 'Обоснование покупателя:' : 'Buyer reason:'}
                  </p>
                  <p className="text-sm text-[#121212]">{respondingDispute?.reason}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setDisputeAction('approve')}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                      disputeAction === 'approve'
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-green-700 border-green-200 hover:border-green-600'
                    }`}
                    data-testid="dispute-action-approve"
                  >
                    {language === 'lv' ? 'Apstiprināt atmaksu' : language === 'ru' ? 'Подтвердить возврат' : 'Approve refund'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDisputeAction('reject')}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                      disputeAction === 'reject'
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-red-700 border-red-200 hover:border-red-600'
                    }`}
                    data-testid="dispute-action-reject"
                  >
                    {language === 'lv' ? 'Noraidīt' : language === 'ru' ? 'Отклонить' : 'Reject'}
                  </button>
                </div>
                <div>
                  <label className="text-xs font-medium text-[#121212] mb-1 block">
                    {language === 'lv'
                      ? (disputeAction === 'reject' ? 'Noraidījuma pamatojums (obligāts, min 10 rakstz.)' : 'Tava atbilde pircējam (neobligāta)')
                      : language === 'ru'
                      ? (disputeAction === 'reject' ? 'Обоснование отклонения (обязательно, мин 10 симв.)' : 'Ваш ответ покупателю (необязательно)')
                      : (disputeAction === 'reject' ? 'Rejection reason (required, min 10 chars)' : 'Your response to buyer (optional)')}
                  </label>
                  <Textarea
                    value={disputeResponse}
                    onChange={(e) => setDisputeResponse(e.target.value)}
                    rows={3}
                    className="text-sm"
                    data-testid="dispute-response-text"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disputeBusy}>
              {language === 'lv' ? 'Aizvērt' : language === 'ru' ? 'Закрыть' : 'Close'}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={disputeBusy || !disputeAction}
              onClick={submitDisputeResponse}
              className={disputeAction === 'approve' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="dispute-response-submit"
            >
              {disputeAction === 'approve'
                ? (language === 'lv'
                    ? `Apstiprināt un atmaksāt €${((respondingDispute?.item_price || 0) * (respondingDispute?.item_quantity || 0)).toFixed(2)}`
                    : language === 'ru'
                    ? `Подтвердить и вернуть €${((respondingDispute?.item_price || 0) * (respondingDispute?.item_quantity || 0)).toFixed(2)}`
                    : `Approve & refund €${((respondingDispute?.item_price || 0) * (respondingDispute?.item_quantity || 0)).toFixed(2)}`)
                : (language === 'lv' ? 'Noraidīt pieprasījumu' : language === 'ru' ? 'Отклонить запрос' : 'Reject request')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Partial-refund (per-item) confirmation dialog */}
      <AlertDialog open={!!refundItem} onOpenChange={(open) => !open && setRefundItem(null)}>
        <AlertDialogContent data-testid={`partial-refund-dialog-${order.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'lv' ? 'Atmaksāt šo preci?' : language === 'ru' ? 'Вернуть этот товар?' : 'Refund this item?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-[#5C5C5C]">
                <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-lg p-3 text-[#121212]">
                  <p className="font-medium">{refundItem?.title}</p>
                  <p className="text-xs text-[#5C5C5C] mt-1">
                    {language === 'lv' ? 'Daudzums' : language === 'ru' ? 'Количество' : 'Quantity'}: {refundItem?.quantity}
                    {' · '}
                    {language === 'lv' ? 'Atmaksas summa' : language === 'ru' ? 'Сумма возврата' : 'Refund amount'}:{' '}
                    <span className="font-bold text-[#D97736]">
                      €{((refundItem?.price || 0) * (refundItem?.quantity || 0)).toFixed(2)}
                    </span>
                  </p>
                </div>
                <p>
                  {language === 'lv'
                    ? 'Automātiski notiks: atmaksa pircējam (karte vai KoLV maciņš), krājuma atjaunošana un e-pasta paziņojums pircējam. Pārējās pasūtījuma preces paliks aktīvas.'
                    : language === 'ru'
                    ? 'Автоматически произойдёт: возврат покупателю (карта или кошелёк KoLV), пополнение остатка и письмо покупателю. Остальные товары заказа остаются активными.'
                    : 'We will automatically: refund the buyer (card or KoLV wallet), restock the listing, and email the buyer. Other order items stay active.'}
                </p>
                <Textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder={language === 'lv'
                    ? 'Atmaksas pamatojums (neobligāts, redzams pircējam)'
                    : language === 'ru'
                    ? 'Причина возврата (необязательно, видно покупателю)'
                    : 'Refund reason (optional, shown to buyer)'}
                  rows={2}
                  className="text-sm"
                  data-testid={`refund-reason-${order.id}`}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={refundBusy}
              data-testid={`refund-dismiss-${order.id}`}
            >
              {language === 'lv' ? 'Atpakaļ' : language === 'ru' ? 'Назад' : 'Back'}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={refundBusy}
              onClick={submitPartialRefund}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`refund-confirm-${order.id}`}
            >
              {language === 'lv'
                ? `Atmaksāt €${((refundItem?.price || 0) * (refundItem?.quantity || 0)).toFixed(2)}`
                : language === 'ru'
                ? `Вернуть €${((refundItem?.price || 0) * (refundItem?.quantity || 0)).toFixed(2)}`
                : `Refund €${((refundItem?.price || 0) * (refundItem?.quantity || 0)).toFixed(2)}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel confirmation dialog — prevents accidental cancellations */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid={`cancel-confirm-dialog-${order.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'lv'
                ? `Atcelt pasūtījumu #${order.id.slice(-6).toUpperCase()}?`
                : language === 'ru'
                ? `Отменить заказ #${order.id.slice(-6).toUpperCase()}?`
                : `Cancel order #${order.id.slice(-6).toUpperCase()}?`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-[#5C5C5C]">
                <p>
                  {language === 'lv'
                    ? 'Šī darbība ir neatgriezeniska. Atcelšanas brīdī automātiski notiks:'
                    : language === 'ru'
                    ? 'Это действие необратимо. При отмене автоматически произойдёт:'
                    : 'This action cannot be undone. On cancellation we will automatically:'}
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    {language === 'lv'
                      ? `Atmaksāt pircējam €${(order.total || 0).toFixed(2)} (kartes atmaksa vai KoLV maciņš)`
                      : language === 'ru'
                      ? `Вернуть покупателю €${(order.total || 0).toFixed(2)} (возврат на карту или кошелёк KoLV)`
                      : `Refund €${(order.total || 0).toFixed(2)} to the buyer (card refund or KoLV wallet)`}
                  </li>
                  <li>
                    {language === 'lv'
                      ? 'Atjaunot preču krājumus sludinājumos'
                      : language === 'ru'
                      ? 'Восстановить остатки товаров в объявлениях'
                      : 'Restore listing inventory'}
                  </li>
                  <li>
                    {language === 'lv'
                      ? 'Nosūtīt e-pastu pircējam ar atmaksas informāciju'
                      : language === 'ru'
                      ? 'Отправить покупателю письмо с деталями возврата'
                      : 'Email the buyer with refund details'}
                  </li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`cancel-dismiss-${order.id}`}>
              {language === 'lv' ? 'Atpakaļ' : language === 'ru' ? 'Назад' : 'Back'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleClick('cancelled')}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid={`cancel-confirm-${order.id}`}
            >
              {language === 'lv' ? 'Jā, atcelt pasūtījumu' : language === 'ru' ? 'Да, отменить заказ' : 'Yes, cancel order'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ship + tracking dialog (seller) */}
      <AlertDialog open={shipOpen} onOpenChange={setShipOpen}>
        <AlertDialogContent data-testid={`ship-dialog-${order.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'lv' ? 'Atzīmēt kā nosūtītu' : language === 'ru' ? 'Отметить как отправленный' : 'Mark as shipped'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'lv'
                ? 'Pievieno sūtījuma izsekošanas numuru (neobligāti) — pircējs to redzēs un varēs izsekot.'
                : language === 'ru'
                ? 'Добавьте номер отслеживания (необязательно) — покупатель увидит его и сможет отследить посылку.'
                : 'Add a tracking number (optional) — the buyer will see it and can track the parcel.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <select
              value={trackingCarrier}
              onChange={(e) => setTrackingCarrier(e.target.value)}
              className="w-full h-11 px-3 rounded-xl border border-[#E5E4E0] bg-white text-sm focus:border-[#D97736] focus:outline-none"
              data-testid={`ship-carrier-${order.id}`}
            >
              <option value="">{language === 'lv' ? 'Piegādātājs (izvēlies)' : language === 'ru' ? 'Перевозчик (выбрать)' : 'Carrier (select)'}</option>
              <option value="Omniva">Omniva</option>
              <option value="DPD">DPD</option>
              <option value="Latvijas Pasts">Latvijas Pasts</option>
              <option value="Venipak">Venipak</option>
              <option value="Cits">{language === 'lv' ? 'Cits' : language === 'ru' ? 'Другой' : 'Other'}</option>
            </select>
            <input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder={language === 'lv' ? 'Izsekošanas numurs' : language === 'ru' ? 'Номер отслеживания' : 'Tracking number'}
              className="w-full h-11 px-3 rounded-xl border border-[#E5E4E0] bg-white text-sm focus:border-[#D97736] focus:outline-none"
              data-testid={`ship-number-${order.id}`}
            />
            <input
              value={trackingUrl}
              onChange={(e) => setTrackingUrl(e.target.value)}
              placeholder={language === 'lv' ? 'Izsekošanas saite (neobligāti)' : language === 'ru' ? 'Ссылка отслеживания (необязательно)' : 'Tracking URL (optional)'}
              className="w-full h-11 px-3 rounded-xl border border-[#E5E4E0] bg-white text-sm focus:border-[#D97736] focus:outline-none"
              data-testid={`ship-url-${order.id}`}
            />
            <p className="text-xs text-[#5C5C5C]">
              {language === 'lv'
                ? 'Ja izvēlies zināmu piegādātāju un ievadi numuru, saite tiks izveidota automātiski.'
                : language === 'ru'
                ? 'Если выбрать известного перевозчика и ввести номер, ссылка создастся автоматически.'
                : 'If you pick a known carrier and enter a number, the tracking link is built automatically.'}
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`ship-dismiss-${order.id}`}>
              {language === 'lv' ? 'Atcelt' : language === 'ru' ? 'Отменить' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmShip}
              className="bg-[#D97736] hover:bg-[#C06528] text-white"
              data-testid={`ship-confirm-${order.id}`}
            >
              {language === 'lv' ? 'Apstiprināt nosūtīšanu' : language === 'ru' ? 'Подтвердить отправку' : 'Confirm shipment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function OrdersPage({ mode = 'buyer' }) {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [disputes, setDisputes] = useState([]); // all disputes visible to current user
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const endpoint = mode === 'seller' ? '/orders/seller' : '/orders';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, disputesRes] = await Promise.all([
        axios.get(`${API}${endpoint}`),
        axios.get(`${API}/disputes`).catch(() => ({ data: [] })),
      ]);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setDisputes(Array.isArray(disputesRes.data) ? disputesRes.data : []);
    } catch (e) {
      console.error(e);
      toast.error(language === 'lv' ? 'Neizdevās ielādēt pasūtījumus' : language === 'ru' ? 'Не удалось загрузить заказы' : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [endpoint, language]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const handleAction = async (orderId, toStatus, note, tracking = null) => {
    try {
      const body = { status: toStatus, note };
      if (tracking) Object.assign(body, tracking);
      const { data } = await axios.put(`${API}/orders/${orderId}/status`, body);
      if (toStatus === 'cancelled') {
        const info = data && data.cancellation_info;
        const method = info && info.method;
        const status = info && info.status;
        if (method === 'wallet' && (status === 'ok' || status === 'already_refunded')) {
          toast.success(language === 'lv'
            ? 'Pasūtījums atcelts. Nauda atmaksāta KoLV maciņā.'
            : language === 'ru'
            ? 'Заказ отменён. Деньги возвращены в кошелёк KoLV.'
            : 'Order cancelled. Refunded to KoLV wallet.');
        } else if (method === 'stripe' && status === 'ok') {
          toast.success(language === 'lv'
            ? 'Pasūtījums atcelts. Atmaksa nosūtīta uz pircēja karti (5–10 darba dienas).'
            : language === 'ru'
            ? 'Заказ отменён. Возврат отправлен на карту покупателя (5–10 рабочих дней).'
            : 'Order cancelled. Refund issued to buyer card (5–10 business days).');
        } else if (method === 'stripe' && status === 'not_paid') {
          toast.success(language === 'lv'
            ? 'Pasūtījums atcelts. Maksājums nebija saņemts — atmaksa nav nepieciešama.'
            : language === 'ru'
            ? 'Заказ отменён. Платёж не был получен — возврат не требуется.'
            : 'Order cancelled. Payment was not captured — no refund needed.');
        } else if (status === 'failed') {
          toast.warning(language === 'lv'
            ? 'Pasūtījums atcelts, taču automātiskā atmaksa neizdevās. Sazināsies ar pircēju manuāli.'
            : language === 'ru'
            ? 'Заказ отменён, но автоматический возврат не удался. С покупателем свяжутся вручную.'
            : 'Order cancelled, but automatic refund failed. Will be processed manually.');
        } else {
          toast.success(language === 'lv' ? 'Pasūtījums atcelts' : language === 'ru' ? 'Заказ отменён' : 'Order cancelled');
        }
      } else {
        toast.success(language === 'lv' ? 'Statuss atjaunots' : language === 'ru' ? 'Статус обновлён' : 'Status updated');
      }
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || (language === 'lv' ? 'Kļūda' : language === 'ru' ? 'Ошибка' : 'Error'));
    }
  };

  const visible = orders.filter((o) => {
    const s = o.status === 'paid' ? 'pending' : (o.status || 'pending');
    if (filter === 'all') return true;
    if (filter === 'active') return !['delivered', 'cancelled'].includes(s);
    if (filter === 'delivered') return s === 'delivered';
    if (filter === 'cancelled') return s === 'cancelled';
    return true;
  });

  const title = mode === 'seller'
    ? (language === 'lv' ? 'Saņemtie pasūtījumi' : language === 'ru' ? 'Полученные заказы' : 'Incoming Orders')
    : (language === 'lv' ? 'Mani pasūtījumi' : language === 'ru' ? 'Мои заказы' : 'My Orders');

  const subtitle = mode === 'seller'
    ? (language === 'lv' ? 'Pasūtījumi, kurus pircēji ir iegādājušies no tavas veikala' : language === 'ru' ? 'Заказы, которые покупатели оформили в вашем магазине' : 'Orders buyers have placed with your shop')
    : (language === 'lv' ? 'Preces un pakalpojumi, ko esi iegādājies' : language === 'ru' ? 'Товары и услуги, которые вы приобрели' : 'Products and services you have purchased');

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid={`orders-page-${mode}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-[#121212] mb-2">{title}</h1>
          <p className="text-[#5C5C5C]">{subtitle}</p>
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {[
            { k: 'all',       lv: 'Visi',       en: 'All',       ru: 'Все' },
            { k: 'active',    lv: 'Aktīvie',    en: 'Active',    ru: 'Активные' },
            { k: 'delivered', lv: 'Piegādātie', en: 'Delivered', ru: 'Доставленные' },
            { k: 'cancelled', lv: 'Atceltie',   en: 'Cancelled', ru: 'Отменённые' },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              className={`text-sm px-4 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
                filter === f.k
                  ? 'bg-[#121212] text-white border-[#121212]'
                  : 'bg-white text-[#5C5C5C] border-[#E5E4E0] hover:border-[#121212]'
              }`}
              data-testid={`filter-${f.k}`}
            >
              {f[language] || f.en}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-white rounded-2xl h-48 animate-pulse" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E5E4E0]">
            <Package className="w-16 h-16 mx-auto mb-4 text-[#5C5C5C] opacity-20" />
            <h3 className="font-outfit text-xl font-semibold text-[#121212] mb-2">
              {language === 'lv' ? 'Nav pasūtījumu' : language === 'ru' ? 'Нет заказов' : 'No orders'}
            </h3>
            <p className="text-[#5C5C5C] mb-6">
              {mode === 'seller'
                ? (language === 'lv' ? 'Šeit parādīsies pircēju pasūtījumi.' : language === 'ru' ? 'Здесь появятся заказы покупателей.' : 'Buyer orders will appear here.')
                : (language === 'lv' ? 'Pārlūko sludinājumus un veic savu pirmo pirkumu!' : language === 'ru' ? 'Смотрите объявления и сделайте первую покупку!' : 'Browse listings and make your first purchase!')
              }
            </p>
            <Link to="/listings">
              <Button variant="outline" className="border-[#D97736] text-[#D97736]">
                {language === 'lv' ? 'Uz sludinājumiem' : language === 'ru' ? 'К объявлениям' : 'Browse listings'}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {visible.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                mode={mode}
                onAction={handleAction}
                onRefresh={load}
                disputes={disputes.filter((d) => d.order_id === o.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
